/**
 * Pure helpers for the "regenerate / retry a failed AI response" flow.
 *
 * When an AI turn fails the backend appends a marker message
 * (`message_type === "regenerate_ai_response"`, `is_user_message: false`,
 * `payload: null`) as the terminal message of the thread. To retry we must
 * re-send `chat/trigger-ai-response` with `regenerate: true` and the id of the
 * ORIGINAL user message the turn was answering — never the marker's id (that
 * would fail with `InvalidLastMessageCannotTriggerAIResponse`).
 *
 * These helpers are kept free of Redux/React so the id-derivation logic can be
 * unit-tested in isolation. `messages` is expected in chronological order
 * (oldest first, latest last) — the same order as `state.threadData.messages`.
 */

export const REGENERATE_MARKER_TYPE = "regenerate_ai_response";

export interface RegenerableMessage {
  id?: string | null;
  message_type?: string | null;
  is_user_message?: boolean | null;
  [key: string]: unknown;
}

/**
 * True when the latest message in the thread is the failure marker, i.e. the
 * current turn failed and a retry affordance should be shown.
 */
export function isFailedTurn(
  messages: RegenerableMessage[] | null | undefined,
): boolean {
  if (!Array.isArray(messages) || messages.length === 0) return false;
  const last = messages[messages.length - 1];
  return last?.message_type === REGENERATE_MARKER_TYPE;
}

export interface RegeneratePayload {
  thread_id: string;
  last_message_id: string;
  regenerate: true;
  button_click_type: string | null;
}

export interface RegeneratePayloadInput {
  threadId?: string | null;
  /** Id remembered when the turn was triggered (preferred). */
  lastTriggeredMessageId?: string | null;
  /** Thread history, used to derive the id when none was remembered (reload). */
  messages?: RegenerableMessage[] | null;
  buttonClickType?: string | null;
}

/**
 * Build the `trigger-ai-response` payload for a retry. Prefers the remembered
 * `lastTriggeredMessageId`, falling back to deriving it from history. Returns
 * null when there is no thread id or no resolvable user-message id — the caller
 * MUST NOT send a retry in that case (it would fail server-side). The payload
 * always carries `regenerate: true` and never implies a duplicate user turn.
 */
export function buildRegeneratePayload(
  input: RegeneratePayloadInput,
): RegeneratePayload | null {
  const { threadId, lastTriggeredMessageId, messages, buttonClickType } = input;
  const lastMessageId = lastTriggeredMessageId || deriveRetryMessageId(messages);
  if (!threadId || !lastMessageId) return null;
  return {
    thread_id: threadId,
    last_message_id: lastMessageId,
    regenerate: true,
    button_click_type: buttonClickType || null,
  };
}

/**
 * Derive the `last_message_id` to send on retry: the id of the last real user
 * chat message that precedes the trailing failure marker. Returns null when the
 * thread is not in a failed state or no such user message exists.
 *
 * Only `is_user_message === true && message_type === "chat"` qualifies — the
 * marker itself and assistant/system messages are skipped by construction.
 */
export function deriveRetryMessageId(
  messages: RegenerableMessage[] | null | undefined,
): string | null {
  if (!isFailedTurn(messages)) return null;

  // Walk backwards from the message just before the trailing marker.
  for (let i = (messages as RegenerableMessage[]).length - 2; i >= 0; i--) {
    const m = (messages as RegenerableMessage[])[i];
    if (m?.is_user_message === true && m?.message_type === "chat" && m?.id) {
      return m.id;
    }
  }
  return null;
}
